import { CircleCheckBig, Inbox, RotateCw, TriangleAlert, Hourglass, Ban } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { WriteOffTab } from '../model/write-off-tabs.js';

/**
 * Yuklanish — kelajakdagi mazmun SHAKLIDA: jadval bo'lsa qatorlar,
 * kartochka bo'lsa kartochkalar. Ma'lumot kelganda sahifa sakramaydi.
 */
export const WriteOffListSkeleton = ({ wide }: { wide: boolean }) =>
  wide ? (
    <div
      aria-hidden
      className="overflow-hidden rounded-2xl border border-border bg-surface shadow-[var(--shadow-1)]"
    >
      <div className="h-10 border-b border-border bg-surface-2" />
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-6 border-b border-border px-5 py-4 last:border-b-0">
          <Skeleton className="h-4 w-28" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-2/3 max-w-72" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
      ))}
    </div>
  ) : (
    <ul aria-hidden className="grid gap-3 md:grid-cols-2">
      {Array.from({ length: 4 }, (_, i) => (
        <li
          key={i}
          className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4 shadow-[var(--shadow-1)]"
        >
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-4 w-1/3" />
        </li>
      ))}
    </ul>
  );

const EMPTY: Record<WriteOffTab, { icon: LucideIcon; title: string; hint: string; tone: string }> = {
  PENDING: {
    icon: CircleCheckBig,
    title: 'writeOff.emptyPending',
    hint: 'writeOff.emptyPendingHint',
    tone: 'bg-success-soft text-success',
  },
  APPROVED: {
    icon: Hourglass,
    title: 'writeOff.emptyApproved',
    hint: 'writeOff.emptyApprovedHint',
    tone: 'bg-surface-2 text-text-muted',
  },
  REJECTED: {
    icon: Ban,
    title: 'writeOff.emptyRejected',
    hint: 'writeOff.emptyRejectedHint',
    tone: 'bg-surface-2 text-text-muted',
  },
  ALL: {
    icon: Inbox,
    title: 'writeOff.empty',
    hint: 'writeOff.emptyHint',
    tone: 'bg-surface-2 text-text-muted',
  },
};

/** Har tab uchun O'Z matni: «kutilayotgan yo'q» — yaxshi xabar, «rad etilgan yo'q» — oddiy fakt. */
export const WriteOffListEmpty = ({ tab }: { tab: WriteOffTab }) => {
  const { icon: Icon, title, hint, tone } = EMPTY[tab];
  return (
    <div className="rounded-2xl border border-dashed border-border-strong bg-surface/60">
      <EmptyState
        icon={
          <span className={cn('flex size-12 items-center justify-center rounded-full', tone)}>
            <Icon size={22} aria-hidden />
          </span>
        }
        title={t(title)}
        description={t(hint)}
        className="py-14 [&>span:first-child]:bg-transparent"
      />
    </div>
  );
};

export const WriteOffListError = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
  <div role="alert" className="rounded-2xl border border-danger/30 bg-danger-soft/50">
    <EmptyState
      icon={
        <span className="flex size-12 items-center justify-center rounded-full bg-danger-soft text-danger">
          <TriangleAlert size={22} aria-hidden />
        </span>
      }
      title={t('writeOff.loadError')}
      description={message}
      action={
        <Button variant="secondary" onClick={onRetry}>
          <RotateCw size={16} aria-hidden />
          {t('writeOff.retry')}
        </Button>
      }
      className="py-14 [&>span:first-child]:bg-transparent"
    />
  </div>
);
